import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, addDoc, serverTimestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { slotUtcToDate, dateLabel, utcSlotTimeRange, utcMinToLocalParts, resolveZone } from '../dateUtils';
import { Icon } from './ui';
import { toast } from './feedback';
import { tapFeedback } from '../native';
import { sendFailToast } from '../sendErrors';
import { sharedRanges, sessionWindows } from '../overlap';

const HOUR = 60 * 60 * 1000;

// Two-hour windows both people are free, starting on the hour in my zone (brief 032).
export const commonSlotsOf = (me, them) => sessionWindows(sharedRanges(me, them), resolveZone(me?.timezone));

// What the partner can teach me: the subjects a request is about.
export const canTeachOf = (me, them) =>
  (them?.strengths || []).filter(s => (me?.wantsToLearn || []).includes(s));

// "New York" from "America/New_York".
const cityOf = zone => (zone || '').split('/').pop().replace(/_/g, ' ');

// Slots worth offering: not starting within the hour, and not a time I already have a
// session (pending or confirmed) at. `taken` holds those sessions' start times in ms.
export const offerableSlots = (slots, taken, now = new Date()) =>
  slots
    .map(slot => ({ slot, when: slotUtcToDate(slot, now) }))
    .filter(({ when }) => when && when.getTime() - now.getTime() >= HOUR && !taken.has(when.getTime()))
    .sort((a, b) => a.when - b.when);

// Ask a partner for a study session at a time you're both free. Used by Matches
// (partner already scored: commonSlots and canTeach given) and by the chat (only
// uid and name: the sheet loads their profile).
function ScheduleSheet({ user, userProfile, partner, onClose, onSent }) {
  const myZone = resolveZone(userProfile?.timezone);
  // From Matches, commonSlots are the shared ranges; offer 2-hour windows inside them.
  const [slots, setSlots] = useState(partner.commonSlots ? sessionWindows(partner.commonSlots, myZone) : null);
  const [subjects, setSubjects] = useState(partner.canTeach || []);
  const [taken, setTaken] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let live = true;
    if (!partner.commonSlots) {
      getDoc(doc(db, 'users', partner.uid))
        .then(snap => {
          if (!live) return;
          const them = snap.data() || {};
          setSlots(commonSlotsOf(userProfile, them));
          setSubjects(canTeachOf(userProfile, them));
        })
        .catch(() => live && setSlots([]));
    }
    Promise.all([
      getDocs(query(collection(db, 'sessions'), where('fromUid', '==', user.uid))),
      getDocs(query(collection(db, 'sessions'), where('toUid', '==', user.uid))),
    ]).then(([mine, theirs]) => {
      if (!live) return;
      const t = new Set();
      [...mine.docs, ...theirs.docs].forEach(d => {
        const s = d.data();
        if (s.status !== 'pending' && s.status !== 'accepted') return;
        const ms = s.sessionDate ? new Date(s.sessionDate).getTime() : s.startMin != null ? slotUtcToDate({ startMin: s.startMin })?.getTime() : null;
        if (ms) t.add(ms);
      });
      setTaken(t);
    }).catch(() => live && setTaken(new Set()));
    return () => { live = false; };
  }, [partner.uid, partner.commonSlots, user.uid, userProfile]);

  const offer = slots && taken ? offerableSlots(slots, taken) : null;

  const send = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'sessions'), {
        fromUid: user.uid, fromName: userProfile.name,
        toUid: partner.uid, toName: partner.name,
        startMin: selected.slot.startMin,
        endMin: selected.slot.endMin,
        sessionDate: selected.when.toISOString(),
        subjects: subjects.slice(0, 2),
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      tapFeedback();
      toast("You'll get a notification when they reply.", { title: `Request sent to ${partner.name}` });
      try {
        const recipSnap = await getDoc(doc(db, 'users', partner.uid));
        if (recipSnap.exists() && recipSnap.data()?.notificationPreferences?.studyRequests !== false) {
          await addDoc(collection(db, 'notifications'), {
            toUid: partner.uid, fromUid: user.uid, fromName: userProfile.name,
            type: 'session_request',
            text: `${userProfile.name} sent you a session request`,
            read: false, createdAt: serverTimestamp(),
          });
        }
      } catch (e) { console.warn('notif write failed', e); }
      onSent && onSent();
      onClose();
    } catch (e) {
      console.error(e);
      sendFailToast('session', e, 'Error sending the request.');
    } finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Schedule with {partner.name}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {!offer ? (
          <p className="muted" style={{ marginBottom: '16px' }}>Finding times you're both free…</p>
        ) : offer.length > 0 ? (
          <>
            <p className="muted" style={{ marginBottom: '16px' }}>Times in your timezone ({cityOf(myZone)}). Pick one to send {partner.name} a request.</p>
            <div className="schedule-slots">
              {offer.map(({ slot, when }) => {
                const on = selected?.slot.startMin === slot.startMin;
                return (
                  <button key={slot.startMin} type="button" className={`schedule-slot ${on ? 'selected' : ''}`} aria-pressed={on} onClick={() => setSelected({ slot, when })}>
                    <span>
                      <strong>{when ? dateLabel(when, myZone) : utcMinToLocalParts(slot.startMin, myZone).dayShort}</strong>
                      <span className="slot-time">{utcSlotTimeRange(slot, myZone)}</span>
                    </span>
                    {on && <Icon name="check" size={20} />}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="muted" style={{ marginBottom: '16px' }}>
            {slots.length ? 'Every time you share is already booked or starts within the hour.' : 'No overlapping times yet.'} Send a message to find a time.
          </p>
        )}
        <button className="btn full" onClick={send} disabled={!selected || sending}>{sending ? 'Sending…' : 'Send request'}</button>
      </div>
    </div>
  );
}
export default ScheduleSheet;
